import {ObjectId} from 'bson';
import {AccountFields} from './fields/keyplay';
import {ScoringSignal, KeyplayScoringSignal} from './signals';
import {StrictOmit, keys, zodEnum} from './util';
import {
  BusinessAudience,
  PrimaryBusinessCategory,
  SecondaryBusinessCategory,
} from './account';
import {z} from 'zod';
import {EnrichedFields} from './fields/enrichment';
import {IntentModelSchema} from './customer';
import {AccountFieldOrCustomField} from './signalDefinition';

export const AccountWithLogoSchema = z.object({
  _id: z.instanceof(ObjectId),
  name: z.string(),
  domain: z.string(),
  logoUrl: z.string().optional(),
});
export type AccountWithLogo = z.infer<typeof AccountWithLogoSchema>;

export const AccountTierLabels = {
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
} as const;

export const AccountTiers = keys(AccountTierLabels);
export const AccountTierSchema = zodEnum(AccountTiers);
export type AccountTier = z.infer<typeof AccountTierSchema>;

export const AccountTierOverride = z.union([
  AccountTierSchema,
  z.literal('none'),
]);
export type AccountTierOverride = z.infer<typeof AccountTierOverride>;

export type Rollup = {
  id: ObjectId;
  label: string;
  signals: KeyplayScoringSignal[];
};

export const ScoringRunSchema = z.object({
  timestamp: z.date(),
  customerId: z.instanceof(ObjectId),
  parentTaskId: z.instanceof(ObjectId),
  activeMarketId: z.instanceof(ObjectId),

  samSize: z.number().optional(),
  lookalikesWithLogos: AccountWithLogoSchema.array().optional(),

  // Snapshot of intent models at scoring time
  // This allows us to know what the models were even if the customer modifies them later
  intentModels: z.array(IntentModelSchema).optional(),
});
export type ScoringRun = z.infer<typeof ScoringRunSchema>;

export interface ScoredAccountMutableState {
  mutated: boolean; // has the state been mutated?

  active: boolean;
  disqualified: boolean;
  excluded: boolean;
  saved: boolean;
  saveDate?: Date;

  displayTier: AccountTier;
  tierOverride: AccountTierOverride;
  tags: ObjectId[];
}

export const CustomerAccountSourceLabels = {
  crm: 'CRM',
  saved: 'Keyplay (Saved)',
  imported: 'Keyplay (Imported)',
  intent: 'Intent',
} as const;

export type CustomerAccountSource = keyof typeof CustomerAccountSourceLabels;

export interface SimilarAccount {
  accountId: ObjectId;
  domain?: string;
  score: number;
}

export type MarketScores = {
  marketId: ObjectId;

  isInSam: boolean;
  matchedSignals: ScoringSignal[];

  overallFit: number;
  tier: AccountTier;
  signalScore: number;
  mostSimilarTo?: SimilarAccount;
};

export const IntentTierLabels = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  none: 'None',
} as const;

export const IntentTiers = keys(IntentTierLabels);
export const IntentTierSchema = zodEnum(IntentTiers);
export type IntentTier = z.infer<typeof IntentTierSchema>;

export type IntentScore = {
  score: number;
  tier?: 'high' | 'medium' | 'low';
};

export interface ScoredAccount {
  timestamp: Date;
  runId: ObjectId;

  // this is the only field we allow to be mutated
  mutatedState: ScoredAccountMutableState;

  originalState: StrictOmit<
    ScoredAccountMutableState,
    // we don't need the original state for these fields
    'displayTier' | 'mutated' | 'saveDate' | 'tags'
  >;

  // used in indexing
  imported: boolean;
  imports: ObjectId[];
  inCrm: boolean;
  intentDate?: Date;
  recommended: boolean;
  matchedSignals: ScoringSignal[];
  overallFit: number;
  similarTo?: ObjectId;

  // scoring data
  fields: AccountFields;
  enrichedFields?: EnrichedFields;

  // TOOD: remove?
  tier: AccountTier;

  // TODO: remove? can be computed from matchedSignals and rollup definitions
  rollups: Rollup[];

  // results for each scoring model
  marketScores: MarketScores[];

  // TODO: turn this into Record<string, IntentScore> to support dynamic sorting. in order to
  // go down that road, we also need to update AccountSort to support specifying which intent
  // model to sort by
  intent?: IntentScore;
  // Intent fields from the intent model that have non-zero values for this account
  // Used for filtering accounts by which intent fields they have data for
  matchedIntentFields?: AccountFieldOrCustomField[];

  // account data
  accountDomain: string;
  accountId: ObjectId;
  accountLinkedIn: string;
  audience?: BusinessAudience[];
  primaryCategory?: PrimaryBusinessCategory[];
  secondaryCategory?: SecondaryBusinessCategory[];
  source?: CustomerAccountSource;

  // Freemium specific
  icpFit?: AccountTier;
  insight?: string;
}

export const MaxFreemiumAccounts = 100;
